import React from "react";
import { Card, CardContainer } from "../../../../components/Card";
import { Box, Typography, useTheme } from "@mui/material";
import { LineChart, PieChart } from "../../../../components/Charts";
import PropTypes from "prop-types";

const formatRevenue = (revenue) => {
    if (revenue >= 10000000) {
        return `${(revenue / 10000000).toFixed(1)}cr`;
    } else if (revenue >= 100000) {
        return `${(revenue / 100000).toFixed(1)}L`;
    } else if (revenue >= 1000) {
        return `${(revenue / 1000).toFixed(1)}k`;
    } else {
        return `${revenue.toLocaleString()}`;
    }
};
const SocialIntelligence = ({ data, type }) => {
    const theme = useTheme();
    const calculateTotal = (key) => {
        return data?.reduce((sum, item) => sum + (item[key] || 0), 0);
    };

    const calculateTotals = (...keys) => {
        return keys.reduce((sum, key) => sum + calculateTotal(key), 0);
    };

    const findLastWeek = (key) => {
        const lw = data?.reduce(
            (acc, item) => {
                if (new Date(acc.week) < new Date(item.week)) {
                    acc = item;
                }
                return acc;
            },
            { week: 0 }
        );
        return lw ? lw[key] || 0 : 0;
    };

    function typeBasedData(data) {
        const dataset = data?.map((item) => ({
            ...item,
            __week_date: new Date(item.week),
        }));

        // if type is weeks, return all data
        if (type === "weeks") {
            return dataset;
        }

        // if type is months, group by month and sum all the numbers || nulls
        const groupedByMonth = dataset.reduce((acc, item) => {
            let yearMonth =
                item.__week_date.getFullYear() +
                "-" +
                (item.__week_date.getMonth() + 1);
            if (!acc[yearMonth]) {
                acc[yearMonth] = [];
            }
            acc[yearMonth].push(item);
            return acc;
        }, {});

        const excludeKeys = ["week", "__week_date", "pk", "id", "updated_at"];

        return Object.values(groupedByMonth).map((weeks) => {
            return weeks.reduce((acc, item) => {
                Object.keys(item).forEach((key) => {
                    if (!excludeKeys.includes(key)) {
                        acc[key] = (acc[key] || 0) + (item[key] || 0);
                    } else {
                        acc[key] = item[key];
                    }
                });
                return acc;
            }, {});
        });
    }

    function typeBasedFormatter(val) {
        if (type === "weeks") {
            return undefined;
        }

        console.log("------->", typeBasedData(data));

        return new Date(val).toLocaleDateString("en-US", {
            month: "short",
            year: "2-digit",
        });
    }

    function getLastWeekOfEachMonth(data) {
        // Convert date strings to Date objects for easier manipulation
        let parsedData = data?.map((item) => ({
            ...item,
            __week_date: new Date(item.week), // Convert `week` string to Date object
        }));

        // Group by year and month
        let groupedByMonth = parsedData.reduce((acc, item) => {
            let yearMonth =
                item.__week_date.getFullYear() +
                "-" +
                (item.__week_date.getMonth() + 1);
            if (!acc[yearMonth]) {
                acc[yearMonth] = [];
            }
            acc[yearMonth].push(item);
            return acc;
        }, {});

        // Get last week of each month
        let lastWeeks = Object.values(groupedByMonth).map((weeks) => {
            return weeks.reduce(
                (latest, current) =>
                    latest.__week_date > current.__week_date ? latest : current,
                { __week_date: new Date(0) }
            );
        });

        // eslint-disable-next-line no-unused-vars
        return lastWeeks.map(({ __week_date, ...rest }) => ({ ...rest }));
    }
    return (
        <div>
            <CardContainer>
                <Box sx={{ flex: "1.1 1.1 0%", width: "100%" }}>
                    <CardContainer>
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Card>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        color: theme.palette.graph.cyan,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                    mb={2}
                                >
                                    {formatRevenue(
                                        calculateTotal(
                                            "social_intelligence_billing"
                                        )
                                    )}
                                </Typography>
                                <Typography
                                    variant="captionRegular"
                                    sx={{
                                        color: theme.palette.neutral[500],
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    Revenue
                                </Typography>
                            </Card>
                            <Card>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        color: theme.palette.graph.yellow,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                    mb={2}
                                >
                                    {formatRevenue(
                                        calculateTotal(
                                            "social_intelligence_new_handles_added"
                                        )
                                    )}
                                </Typography>
                                <Typography
                                    variant="captionRegular"
                                    sx={{
                                        color: theme.palette.neutral[500],
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    New Handles Added
                                </Typography>
                            </Card>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Card>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        color: theme.palette.graph.magenta,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                    mb={2}
                                >
                                    {formatRevenue(
                                        calculateTotal(
                                            "social_intelligence_lists"
                                        )
                                    )}
                                </Typography>
                                <Typography
                                    variant="captionRegular"
                                    sx={{
                                        color: theme.palette.neutral[500],
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    Total Lists
                                </Typography>
                            </Card>
                            <Card>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        color: theme.palette.graph.purple,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                    mb={2}
                                >
                                    {formatRevenue(
                                        calculateTotals(
                                            "social_intelligence_campaign_dashboards",
                                            "social_intelligence_competitor_analysis",
                                            "social_intelligence_brand_intelligence",
                                            "social_intelligence_insights"
                                        )
                                    )}
                                </Typography>
                                <Typography
                                    variant="captionRegular"
                                    sx={{
                                        color: theme.palette.neutral[500],
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    Total Dashboards Generated
                                </Typography>
                            </Card>
                        </Box>
                    </CardContainer>
                    <Card>
                        <Typography
                            variant="h5"
                            sx={{
                                color: theme.palette.graph.pink,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                            mb={2}
                        >
                            {formatRevenue(
                                findLastWeek("customer_social_intelligence")
                            )}
                        </Typography>
                        <Typography
                            variant="captionRegular"
                            sx={{
                                color: theme.palette.neutral[500],
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            Overall Cumulative Customers
                        </Typography>
                    </Card>
                </Box>
                <PieChart
                    data={data}
                    series={[
                        {
                            key: "social_intelligence_campaign_dashboards",
                            name: "Campaign",
                        },
                        {
                            key: "social_intelligence_insights",
                            name: "Insights ",
                        },
                        {
                            key: "social_intelligence_competitor_analysis",
                            name: "Competitor ",
                        },
                        {
                            key: "social_intelligence_brand_intelligence",
                            name: "Brand ",
                        },
                    ]}
                    title="Overview of Dashboards Generated"
                />
            </CardContainer>
            <CardContainer>
                <LineChart
                    data={getLastWeekOfEachMonth(data)}
                    series={[
                        {
                            name: "Customers",
                            key: "customer_social_intelligence",
                        },
                    ]}
                    xKey="week"
                    xFormatter={(val) =>
                        new Date(val).toLocaleDateString("en-US", {
                            month: "short",
                            year: "2-digit",
                        })
                    }
                    // yLabel="Seats Sold"
                    title="Customer Growth"
                />
                <LineChart
                    data={typeBasedData(data)}
                    series={[
                        {
                            name: "Revenue",
                            key: "social_intelligence_billing",
                        },
                    ]}
                    xKey="week"
                    // yLabel="Seats Sold"
                    xFormatter={typeBasedFormatter}
                    title="Revenue Growth"
                />
            </CardContainer>
            <CardContainer>
                <LineChart
                    data={typeBasedData(data)?.map((item) => ({
                        ...item,
                        SID:
                            (item.social_intelligence_campaign_dashboards ||
                                0) +
                            (item.social_intelligence_competitor_analysis ||
                                0) +
                            (item.social_intelligence_brand_intelligence || 0) +
                            (item.social_intelligence_insights || 0),
                    }))}
                    series={[
                        {
                            name: "Dashboard",
                            key: "SID",
                        },
                    ]}
                    xKey="week"
                    // yLabel="Seats Sold"
                    xFormatter={typeBasedFormatter}
                    title="Dashboard Growth"
                />
                <LineChart
                    data={typeBasedData(data)}
                    series={[
                        {
                            name: "Handles",
                            key: "social_intelligence_new_handles_added",
                        },
                    ]}
                    xKey="week"
                    // yLabel="Seats Sold"
                    xFormatter={typeBasedFormatter}
                    title="Handles Growth"
                />
            </CardContainer>
        </div>
    );
};
SocialIntelligence.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    type: PropTypes.string.isRequired,
};

export default SocialIntelligence;
